/**
 * Utilitários para otimização de imagens
 */

/**
 * Gera um blur data URL para placeholder
 * @param width - Largura do placeholder
 * @param height - Altura do placeholder
 * @param color - Cor de fundo (padrão: #f3f4f6)
 * @returns Data URL do placeholder
 */
export const generateBlurDataURL = (
  width: number = 40,
  height: number = 40,
  color: string = '#f3f4f6'
): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) return '';
  
  canvas.width = width;
  canvas.height = height;
  
  // Criar gradiente suave
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, color + '80'); // 50% opacity
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  return canvas.toDataURL('image/jpeg', 0.1);
};

const getSupabaseRenderUrl = (url: string): string => {
  if (url.includes('supabase') && url.includes('/object/public/')) {
    return url.replace('/object/public/', '/render/image/public/');
  }
  return url;
};

/**
 * Gera URLs otimizadas para diferentes tamanhos de tela
 * @param baseUrl - URL base da imagem
 * @param sizes - Array de tamanhos desejados
 * @returns Objeto com URLs otimizadas
 */
export const generateResponsiveImageUrls = (
  baseUrl: string,
  sizes: number[] = [320, 640, 1024, 1920]
) => {
  // Implementação via wsrv.nl (CDN gratuita patrocinada pela Cloudflare)
  if (baseUrl.includes('supabase') || baseUrl.includes('http')) {
    return sizes.map(size => {
      const params = new URLSearchParams();
      params.set('url', baseUrl);
      params.set('w', size.toString());
      params.set('q', '80');
      params.set('output', 'webp');
      
      return {
        width: size,
        url: `https://wsrv.nl/?${params.toString()}`
      };
    });
  }
  
  // Para outras URLs, retornar a URL original
  return sizes.map(size => ({
    width: size,
    url: baseUrl
  }));
};

/**
 * Memory cache of preloaded image URLs to avoid duplicate network requests
 */
const PRELOADED_URLS_CACHE = new Set<string>();

/**
 * Preload an optimized image URL into browser cache
 */
export const preloadImage = (url: string): Promise<void> => {
  if (!url || PRELOADED_URLS_CACHE.has(url)) return Promise.resolve();

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      PRELOADED_URLS_CACHE.add(url);
      resolve();
    };
    img.onerror = () => {
      // Resolve anyway so errors don't block other preloads
      resolve();
    };
    img.decoding = 'async';
    img.src = url;
  });
};

/**
 * Gera WebP URL com fallback otimizado via CDN global Cloudflare/wsrv
 * @param originalUrl - URL original da imagem
 * @param quality - Qualidade do WebP (1-100, padrão: 80)
 * @param width - Largura desejada (opcional)
 * @param height - Altura desejada (opcional)
 * @returns Objeto com WebP e fallback URLs
 */
export const generateWebPWithFallback = (
  originalUrl: string,
  quality: number = 80,
  width?: number,
  height?: number
) => {
  if (!originalUrl) {
    return {
      webp: '',
      fallback: '',
      isOptimized: false
    };
  }

  // Safe parameter handling in case quality was accidentally passed as width (e.g. 300, 800)
  let validQuality = quality;
  let targetWidth = width;
  let targetHeight = height;

  if (quality > 100) {
    if (!targetWidth) {
      targetWidth = quality;
    }
    validQuality = 80;
  }

  // Clamp quality between 10 and 95
  validQuality = Math.max(10, Math.min(95, validQuality));

  // Implementação via wsrv.nl para otimização de imagens sem custo adicional
  if (originalUrl.includes('supabase') || originalUrl.includes('http')) {
    const params = new URLSearchParams();
    params.set('url', originalUrl);
    params.set('q', validQuality.toString());
    params.set('we', 'true'); // Without enlargement (don't upscale smaller images)
    params.set('il', 'true'); // Interlace / progressive scan for faster perceived load
    params.set('af', 'true'); // Adaptive filter for best WebP compression

    if (targetWidth) params.set('w', Math.round(targetWidth).toString());
    if (targetHeight) params.set('h', Math.round(targetHeight).toString());

    // Fit cover para evitar distorção quando ambas dimensões são informadas
    if (targetWidth && targetHeight) {
      params.set('fit', 'cover');
      params.set('a', 'center');
    }

    // URL otimizada forçada em WebP
    const webpParams = new URLSearchParams(params);
    webpParams.set('output', 'webp');
    const webpUrl = `https://wsrv.nl/?${webpParams.toString()}`;

    // Fallback URL (usa a CDN com formato original para máxima compatibilidade)
    const fallbackUrl = `https://wsrv.nl/?${params.toString()}`;

    return {
      webp: webpUrl,
      fallback: fallbackUrl,
      isOptimized: true
    };
  }

  // Para Cloudinary
  if (originalUrl.includes('cloudinary')) {
    const webpUrl = originalUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    return {
      webp: webpUrl,
      fallback: originalUrl,
      isOptimized: true
    };
  }

  // Para outras URLs, retornar original
  return {
    webp: originalUrl,
    fallback: originalUrl,
    isOptimized: false
  };
};

/**
 * Calcula tamanhos responsivos para o atributo sizes
 * @param breakpoints - Breakpoints personalizados
 * @returns String de tamanhos para o atributo sizes
 */
export const generateSizesAttribute = (
  breakpoints: { [key: string]: string } = {
    '(max-width: 768px)': '100vw',
    '(max-width: 1024px)': '50vw',
    '(max-width: 1280px)': '33vw',
    'default': '25vw'
  }
): string => {
  return Object.entries(breakpoints)
    .map(([query, size]) => `${query} ${size}`)
    .join(', ');
};

/**
 * Gera URLs responsivas para diferentes tamanhos de tela
 * @param baseUrl - URL base da imagem
 * @param sizes - Array de tamanhos desejados
 * @param quality - Qualidade das imagens
 * @returns Array de objetos com srcSet e sizes
 */
export const generateResponsiveImageSet = (
  baseUrl: string,
  sizes: number[] = [320, 640, 1024, 1920],
  quality: number = 85
) => {
  const webpSet = generateWebPWithFallback(baseUrl, quality);
  
  if (webpSet.isOptimized) {
    // Para Supabase Storage
    const webpSrcSet = sizes
      .map(size => {
        const optimized = generateWebPWithFallback(baseUrl, quality, size);
        return `${optimized.webp} ${size}w`;
      })
      .join(', ');
    
    const fallbackSrcSet = sizes
      .map(size => {
        const optimized = generateWebPWithFallback(baseUrl, quality, size);
        return `${optimized.fallback} ${size}w`;
      })
      .join(', ');
    
    return {
      webp: webpSrcSet,
      fallback: fallbackSrcSet,
      sizes: generateSizesAttribute()
    };
  }
  
  // Para URLs não otimizadas
  return {
    webp: baseUrl,
    fallback: baseUrl,
    sizes: '100vw'
  };
};

/**
 * Gera configurações otimizadas para diferentes contextos de uso
 * @param context - Contexto de uso da imagem
 * @returns Configurações otimizadas
 */
export const getImageConfigForContext = (context: 'gallery' | 'card' | 'lightbox' | 'thumbnail') => {
  const configs = {
    gallery: {
      sizes: [400, 800, 1200, 1920],
      quality: 85,
      sizesAttribute: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
    },
    card: {
      sizes: [320, 640, 1024],
      quality: 80,
      sizesAttribute: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw'
    },
    lightbox: {
      sizes: [800, 1200, 1920, 2560],
      quality: 90,
      sizesAttribute: '100vw'
    },
    thumbnail: {
      sizes: [150, 300, 600],
      quality: 75,
      sizesAttribute: '(max-width: 768px) 25vw, 15vw'
    }
  };
  
  return configs[context];
};

/**
 * Preload de imagens em background
 * @param urls - Array de URLs para preload
 * @param onProgress - Callback de progresso
 * @returns Promise que resolve quando todas as imagens são carregadas
 */
export const preloadImages = (
  urls: string[],
  onProgress?: (loaded: number, total: number) => void
): Promise<HTMLImageElement[]> => {
  let loadedCount = 0;
  
  return Promise.all(
    urls.map(url => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          loadedCount++;
          onProgress?.(loadedCount, urls.length);
          resolve(img);
        };
        
        img.onerror = reject;
        img.src = url;
      });
    })
  );
};

/**
 * Verifica se o navegador suporta WebP
 * @returns Promise que resolve com suporte a WebP
 */
export const supportsWebP = (): Promise<boolean> => {
  return new Promise(resolve => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

/**
 * Hook para detectar suporte a WebP
 * Nota: Este hook deve ser usado em componentes React
 */
export const useWebPSupport = () => {
  // Este hook será implementado em um arquivo separado de hooks
  // para evitar dependência do React em utilitários
  return null;
};
